import { useState } from "react";
import { Box, Button, Paper, Typography, useTheme } from "@mui/material";
import {
    File,
    FolderOpen,
    ClipboardList,
    CloseCircle,
    Bookmark,
    DocumentText,
} from "@solar-icons/react/ssr";
import { ShortcutPopup } from "@/project/ProjectShortcuts";

/**
 * ActionsPanel — action buttons for workflows.
 *
 * In project-scoped mode (horizontal):
 *  - brief/wrap_report: Download Notes + Close
 *  - bid: Close only
 *  - Space-between layout so Close is always at bottom-right
 *
 * In global mode (vertical/full — Phase 2):
 *  - All four buttons: Download, Create Project, Create Bid, Close
 *
 * Props:
 *  - waiting: boolean
 *  - briefCreated: boolean
 *  - projectCreated: boolean
 *  - bidCreated: boolean
 *  - onDownloadBrief: () => void
 *  - onCreateProject: () => void
 *  - onCreateBid: () => void
 *  - onClose: () => void
 *  - agentType: string — "brief" | "wrap_report" | "bid"
 *  - horizontal: boolean — if true, render as a thin horizontal bar (project-scoped)
 *  - shortcutDefaults: { name, url, shortcut_type } — pre-filled shortcut for the popup
 *  - projectId: number — project ID for shortcut creation
 *  - onAddShortcut: (createdShortcut) => void — called after successful shortcut creation
 */
export default function ActionsPanel({
    waiting,
    briefCreated,
    projectCreated,
    bidCreated,
    shortcutAdded,
    onDownloadBrief,
    onDownloadSheet,
    onCreateProject,
    onCreateBid,
    onAddShortcut,
    onClose,
    agentType,
    horizontal = false,
    shortcutDefaults,
    projectId,
}) {
    const theme = useTheme();

    // ─── Shortcut popup state ───────────────────────────────────────────
    const [pendingShortcut, setPendingShortcut] = useState(null);

    const handleOpenShortcutPopup = () => {
        setPendingShortcut(shortcutDefaults || { name: "", url: "" });
    };

    const handleShortcutChange = (shortcut) => {
        setPendingShortcut(shortcut);
    };

    const handleShortcutCreated = (createdShortcut) => {
        onAddShortcut && onAddShortcut(createdShortcut);
        setPendingShortcut(null);
    };

    const isCanvasWorkflow =
        agentType === "brief" ||
        agentType === "wrap_report" ||
        agentType === "timeline" ||
        agentType === "intake";

    const intakeProjectNotCreated = agentType == "intake" && !projectCreated;

    const shortcutPopup = pendingShortcut && (
        <ShortcutPopup
            open={!!pendingShortcut}
            shortcut={pendingShortcut}
            handleClose={() => setPendingShortcut(null)}
            onShortcutChange={handleShortcutChange}
            onShortcutCreate={handleShortcutCreated}
            projectId={projectId}
        />
    );

    if (horizontal) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                {/* Left: workflow-specific actions */}
                <Box sx={{ display: "flex", gap: 0.5 }}>
                    {isCanvasWorkflow && agentType != "timeline" && (
                        <Button
                            startIcon={<ClipboardList size={14} />}
                            size="small"
                            variant="contained"
                            onClick={onDownloadBrief}
                            disabled={!briefCreated}
                        >
                            Download doc
                        </Button>
                    )}
                    {agentType === "timeline" && onDownloadSheet && (
                        <Button
                            startIcon={<DocumentText size={14} />}
                            size="small"
                            variant="contained"
                            onClick={onDownloadSheet}
                            disabled={!briefCreated}
                        >
                            Download xlsx
                        </Button>
                    )}
                    {isCanvasWorkflow && onAddShortcut && (
                        <Button
                            startIcon={<Bookmark size={14} />}
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={handleOpenShortcutPopup}
                            disabled={
                                !briefCreated ||
                                shortcutAdded ||
                                intakeProjectNotCreated
                            }
                            sx={{ textTransform: "none" }}
                        >
                            {shortcutAdded
                                ? "Shortcut Added"
                                : "Add to Shortcuts"}
                        </Button>
                    )}
                    {agentType === "intake" && (
                        <Button
                            startIcon={<File size={14} />}
                            size="small"
                            variant="contained"
                            disabled={
                                waiting || projectCreated || !briefCreated
                            }
                            onClick={onCreateProject}
                            color="secondary"
                        >
                            Submit
                        </Button>
                    )}
                </Box>

                {/* Right: close button */}
                {onClose && (
                    <Button
                        startIcon={<CloseCircle size={14} />}
                        size="small"
                        variant="contained"
                        color="inherit"
                        onClick={onClose}
                        sx={{ textTransform: "none" }}
                    >
                        Close Assistant
                    </Button>
                )}

                {shortcutPopup}
            </Box>
        );
    }

    // ─── Full vertical layout (Phase 2 — global mode) ───────────────────
    return (
        <Paper
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                borderRadius: 3,
                background: theme.palette.background.default,
                overflow: "hidden",
                p: 2,
            }}
        >
            <Box>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                >
                    Actions
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Button
                        startIcon={<ClipboardList size={14} />}
                        size="small"
                        variant="contained"
                        onClick={onDownloadBrief}
                        disabled={!briefCreated}
                        sx={{
                            backgroundColor: "#21A468",
                            textTransform: "none",
                        }}
                    >
                        Download Notes
                    </Button>
                    <Button
                        startIcon={<File size={14} />}
                        size="small"
                        variant="contained"
                        disabled={waiting || projectCreated || !briefCreated}
                        onClick={onCreateProject}
                        sx={{
                            backgroundColor: "#0082E4",
                            textTransform: "none",
                        }}
                    >
                        Create Project
                    </Button>
                    <Button
                        startIcon={<FolderOpen size={14} />}
                        size="small"
                        variant="contained"
                        disabled={waiting || bidCreated || !projectCreated}
                        onClick={onCreateBid}
                        sx={{
                            backgroundColor: "#D08D28",
                            textTransform: "none",
                        }}
                    >
                        Create Bid
                    </Button>
                </Box>
            </Box>

            {onClose && (
                <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                    <Button
                        startIcon={<CloseCircle size={14} />}
                        size="small"
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            backgroundColor: "#D08D28",
                            textTransform: "none",
                        }}
                    >
                        Close Assistant
                    </Button>
                </Box>
            )}

            {shortcutPopup}
        </Paper>
    );
}
